import React, { Component } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Tooltip } from 'react-tooltip';
import { Colors } from 'modules/theme';
import { push } from 'modules/history';
import H3 from 'components/H3';
import H4 from 'components/H4';
import H5 from 'components/H5';
import H2 from 'components/H2';
import Col from 'components/Col';
import Row from 'components/Row';
import Line from 'components/Line';
import Icon from 'components/Icon';
import IconButton from 'components/IconButton';
import BG from 'components/BG';
import ListEmpty from 'components/ListEmpty';
import Loader from 'components/Loader';
import Modal from 'components/Modal';
import { KineticoRolesCode } from 'constants/index';
import {
  CHECK_KEY_LABELS,
  EXECUTION_SOURCE_LABELS,
  formatTriggeredBy,
} from 'routes/HealthChecks/HealthCheckDetail/constants';
import WF from './WF';
import WS from './WS';
import RO from './RO';
import ModalCsvFiles from './ModalCsvFiles';
import LocationAlertCard from './LocationAlertCard';
import LocationAlertsModal from './LocationAlertsModal';

const Container = styled.div``;

const MANUAL_HEALTH_CHECK_ATTEMPT_WINDOW_MINUTES = 30;

const LastHealthCheckTooltipStyles = createGlobalStyle`
  [data-tooltip-id="last-health-check-tooltip"].last-health-check-tooltip,
  .last-health-check-tooltip {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    padding: 0 !important;
    opacity: 1 !important;
  }
`;

// Drawer components
const DrawerOverlay = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  opacity: ${props => (props.isVisible ? 1 : 0)};
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  transition: opacity 0.4s cubic-bezier(0.215, 0.61, 0.355, 1),
    visibility 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
  will-change: opacity, visibility;
  backdrop-filter: blur(${props => (props.isVisible ? '2px' : '0px')});
`;

const DrawerContainer = styled.div`
  position: fixed;
  top: 0;
  right: 10px;
  width: 400px;
  height: calc(100vh - 20px);
  margin-top: 10px;
  margin-bottom: 10px;
  background-color: #f9f9f9;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.08);
  z-index: 1055;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  transform: translateX(${props => (props.isVisible ? '0' : 'calc(100% + 30px)')});
  transition: transform 0.4s cubic-bezier(0.215, 0.61, 0.355, 1), box-shadow 0.4s ease;
  will-change: transform, box-shadow;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    pointer-events: none;
    box-shadow: ${props => (props.isVisible ? '-3px 0 15px rgba(0, 0, 0, 0.03)' : 'none')};
    transition: box-shadow 0.4s ease;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background-color: #fefefe;
  border-bottom: 1px solid ${Colors.gray2};
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  opacity: ${props => (props.isVisible ? 1 : 0.8)};
  transform: translateY(${props => (props.isVisible ? '0' : '-10px')});
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
  will-change: opacity, transform;
  position: sticky;
  top: 0;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 5px;
`;

const DrawerContent = styled.div`
  padding: 0 20px 20px 20px;
  background-color: #f9f9f9;
  opacity: ${props => (props.isVisible ? 1 : 0.8)};
  transform: translateX(${props => (props.isVisible ? '0' : '20px')});
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.215, 0.61, 0.355, 1);
  transition-delay: ${props => (props.isVisible ? '0.1s' : '0s')};
  will-change: opacity, transform;
  overflow-y: auto;
  flex: 1;
  -webkit-overflow-scrolling: touch; /* for smooth scrolling on iOS */
  padding-top: 5px;

  /* Add a padding to the bottom for better scrolling experience */
  &:after {
    content: '';
    display: block;
    height: 20px;
  }
`;

const CardSection = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  padding: 20px;
  margin-bottom: 20px;
  transform: translateY(${props => (props.isVisible ? '0' : '15px')});
  opacity: ${props => (props.isVisible ? 1 : 0.7)};
  transition: transform 0.5s cubic-bezier(0.215, 0.61, 0.355, 1),
    opacity 0.4s cubic-bezier(0.215, 0.61, 0.355, 1), box-shadow 0.3s ease;
  transition-delay: ${props => (props.index ? `${0.1 + props.index * 0.07}s` : '0.1s')};
  will-change: transform, opacity, box-shadow;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-top: 0;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  align-items: center;
`;

const InfoLabel = styled.span`
  color: ${Colors.gray8};
  font-size: 14px;
  font-weight: 400;
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
`;

// Original styled components
const InstallationNotCompleteContainer = styled.div`
  margin-top: 100px;
  margin-bottom: 100px;
`;

const membraneLife = [
  { name: 'nocomplete', value: 50, fill: Colors.grayChart },
  { name: 'complete', value: 50, fill: Colors.blue },
];

// Add a function to format phone numbers
const formatPhoneNumber = phoneNumber => {
  // Remove all non-digit characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check if the input is valid
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  // If the phone number isn't in a standard format, return original
  return phoneNumber;
};

// Create a styled status label component
const StatusLabel = styled.span`
  color: ${props => (props.installed ? '#4CAF50' : '#FF0000')};
  font-size: 14px;
  font-weight: 500;
`;

const CustomSelect = styled.select`
  background: transparent;
  border: none;
  color: ${props => (props.value === 'true' ? '#4CAF50' : '#FF0000')};
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg fill='gray' height='24' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><path d='M7 10l5 5 5-5z'/></svg>");
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 0px;
  padding-right: 25px;
  margin-right: -5px;

  &:focus {
    outline: none;
  }

  option {
    color: #333;
  }
`;

// Add a dropdown menu for Quick Actions
const QuickActionsDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
  border-radius: 4px;
  overflow: hidden;
  display: ${props => (props.isOpen ? 'block' : 'none')};
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  text-decoration: none;
  display: flex;
  align-items: center;
  color: #333;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }

  i {
    margin-right: 8px;
  }
`;

// Add a disabled version of DropdownItem
const DisabledDropdownItem = styled(DropdownItem)`
  color: ${Colors.gray3};
  cursor: not-allowed;

  &:hover {
    background-color: transparent;
  }
`;

// Add styled select dropdown for the breadcrumbs
const BreadcrumbWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: ${Colors.black};
  font-weight: 600;
  font-size: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
  min-width: 150px;
  padding-right: 8px;
`;

const BreadcrumbSelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
`;

// Add a styled close button near the top of the file
const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${Colors.gray2};
  }
`;

// Add a style tag to the top of the file for the global style
const NoScrollStyle = styled.style`
  body {
    overflow: hidden;
    padding-right: ${window.innerWidth - document.documentElement.clientWidth}px;
  }
`;

export default class WaterFilterForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filterHeadSelected: '',
      showContactInfo: false,
      alertsModal: false,
      quickActionsOpen: false,
      lastUpdated: null,
      lastUpdatedLoading: false,
      tdsValue: null,
      tdsLoading: false,
      modalDatePickerCsv: false,
      datePickerFromCsv: moment()
        .add(-7, 'days')
        .toDate(),
      datePickerToCsv: moment().toDate(),
      csvType: 1,
      csvTypeString: 'raw_data',
      isDisconnected: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle body scroll locking when drawer opens/closes
    if (prevState.showContactInfo !== this.state.showContactInfo) {
      if (this.state.showContactInfo) {
        // Save current scroll position
        this.scrollPosition = window.pageYOffset;
      } else {
        // Restore scroll position when drawer closes
        setTimeout(() => {
          window.scrollTo(0, this.scrollPosition || 0);
        }, 50);
      }
    }
  }

  switchByFilterHead = filterHead => {
    const { location, allAlerts, fetchAllAlerts, setDeviceId } = this.props;

    switch (filterHead) {
      case 'WaterFilterHead':
        return (
          <WF
            location={location}
            allAlerts={allAlerts}
            fetchAllAlerts={fetchAllAlerts}
            renderInstructionsModal={this.renderInstructionsModal}
            setDeviceId={setDeviceId}
            updateLastUpdated={this.updateLastUpdated}
            setLastUpdatedLoading={this.setLastUpdatedLoading}
            setDeviceDisconnected={this.setDeviceDisconnected}
          />
        );
      case 'ReverseOsmosisFilterHead':
        return (
          <RO
            location={location}
            membraneLife={membraneLife}
            renderInstructionsModal={this.renderInstructionsModal}
            allAlerts={allAlerts}
            fetchAllAlerts={fetchAllAlerts}
            setDeviceId={setDeviceId}
            updateLastUpdated={this.updateLastUpdated}
            setLastUpdatedLoading={this.setLastUpdatedLoading}
            updateTdsValue={this.updateTdsValue}
            setTdsLoading={this.setTdsLoading}
            setDeviceDisconnected={this.setDeviceDisconnected}
          />
        );
      case 'SoftenerFilterHead':
        return (
          <WS
            location={location}
            renderInstructionsModal={this.renderInstructionsModal}
            allAlerts={allAlerts}
            fetchAllAlerts={fetchAllAlerts}
            setDeviceId={setDeviceId}
            updateLastUpdated={this.updateLastUpdated}
            setLastUpdatedLoading={this.setLastUpdatedLoading}
          />
        );
      default:
        return (
          <InstallationNotCompleteContainer>
            <ListEmpty message="There are no devices configured" />
          </InstallationNotCompleteContainer>
        );
    }
  };

  renderContactInfoDrawer = () => {
    const { showContactInfo } = this.state;
    const { location, role_id } = this.props;
    const {
      brand_name,
      account_name,
      identificator,
      contact_first_name,
      contact_last_name,
      contact_email,
      contact_phone_number,
      full_address,
      installed,
      installed_date,
      WaterFilterHead,
      ReverseOsmosisFilterHead,
      SoftenerFilterHead,
    } = location;

    // Get the current active filter head for configuration details
    let activeFilterHead = null;
    let configInfo = null;
    let deviceType = null;
    let deviceId = null;
    let membraneChangeDate = null;

    if (ReverseOsmosisFilterHead) {
      deviceType = 'Reverse Osmosis';
      deviceId = ReverseOsmosisFilterHead.device_id;
      configInfo = ReverseOsmosisFilterHead.ReverseOsmosisFilterConfiguration?.name;
      membraneChangeDate = ReverseOsmosisFilterHead.last_membrane_change_date;
      activeFilterHead = ReverseOsmosisFilterHead;
    } else if (WaterFilterHead) {
      deviceType = 'Water Filter';
      deviceId = WaterFilterHead.device_id;
      configInfo = WaterFilterHead.WaterFilterConfiguration?.name;
      activeFilterHead = WaterFilterHead;
    } else if (SoftenerFilterHead) {
      deviceType = 'Softener';
      deviceId = SoftenerFilterHead.device_id;
      configInfo = SoftenerFilterHead.SoftenerConfiguration?.name;
      activeFilterHead = SoftenerFilterHead;
    }

    return (
      <>
        {showContactInfo && <NoScrollStyle />}
        <DrawerOverlay
          isVisible={showContactInfo}
          onClick={() => this.switchModal('showContactInfo')}
        />
        <DrawerContainer isVisible={showContactInfo}>
          <DrawerHeader isVisible={showContactInfo}>
            <H3 color={Colors.black} weight={600}>
              {brand_name}
            </H3>
            <CloseButton onClick={() => this.switchModal('showContactInfo')}>
              <Icon name="times" color={Colors.gray8} fontSize={16} />
            </CloseButton>
          </DrawerHeader>
          <DrawerContent isVisible={showContactInfo}>
            <CardSection isVisible={showContactInfo} index={0}>
              <CardTitle>Location Details</CardTitle>

              <InfoRow>
                <InfoLabel>Account Name</InfoLabel>
                <InfoValue>{account_name}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Location ID</InfoLabel>
                <InfoValue style={{ fontFamily: 'Inter, sans-serif' }}>{identificator}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>{full_address}</InfoValue>
              </InfoRow>
            </CardSection>

            <CardSection isVisible={showContactInfo} index={1}>
              <CardTitle>Contact Information</CardTitle>

              <InfoRow>
                <InfoLabel>Name</InfoLabel>
                <InfoValue>{`${contact_first_name} ${contact_last_name}`}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{contact_email}</InfoValue>
              </InfoRow>

              <InfoRow>
                <InfoLabel>Phone</InfoLabel>
                <InfoValue>{formatPhoneNumber(contact_phone_number)}</InfoValue>
              </InfoRow>
            </CardSection>

            <CardSection isVisible={showContactInfo} index={2}>
              <CardTitle>Device Status</CardTitle>

              <InfoRow>
                <InfoLabel>Install status</InfoLabel>
                {role_id === 1 ? (
                  <CustomSelect
                    value={installed.toString()}
                    onChange={e =>
                      this.handleChangeInstallationStatus({
                        target: {
                          value: e.target.value,
                          name: 'installationStatus',
                        },
                      })
                    }
                  >
                    <option value="true">Complete</option>
                    <option value="false">Not Complete</option>
                  </CustomSelect>
                ) : (
                  <StatusLabel installed={installed}>
                    {installed ? 'Complete' : 'Not Complete'}
                  </StatusLabel>
                )}
              </InfoRow>

              {deviceId && (
                <InfoRow>
                  <InfoLabel>Device ID</InfoLabel>
                  <InfoValue>{deviceId}</InfoValue>
                </InfoRow>
              )}

              {installed_date && (
                <InfoRow>
                  <InfoLabel>Installed date</InfoLabel>
                  <InfoValue>{moment(installed_date).format('MMM DD, YYYY')}</InfoValue>
                </InfoRow>
              )}
            </CardSection>

            {installed && activeFilterHead && (
              <CardSection isVisible={showContactInfo} index={3}>
                <CardTitle>Configuration Details</CardTitle>

                {deviceType && (
                  <InfoRow>
                    <InfoLabel>Device Type</InfoLabel>
                    <InfoValue>{deviceType}</InfoValue>
                  </InfoRow>
                )}

                {configInfo && (
                  <InfoRow>
                    <InfoLabel>Configuration</InfoLabel>
                    <InfoValue>{configInfo}</InfoValue>
                  </InfoRow>
                )}

                {deviceType === 'Reverse Osmosis' && membraneChangeDate && (
                  <InfoRow>
                    <InfoLabel>Last Membrane Change</InfoLabel>
                    <InfoValue>{moment(membraneChangeDate).format('MMM DD, YYYY')}</InfoValue>
                  </InfoRow>
                )}
              </CardSection>
            )}
          </DrawerContent>
        </DrawerContainer>
      </>
    );
  };

  renderInstructionsModal = (isVisible, onClose, installSteps) => (
    <Modal title="Installation Steps" onClose={onClose} isVisible={isVisible}>
      {installSteps.map(step => (
        <>
          <Row justifyContent="flex-start" alignItems="start" style={{ padding: 10 }}>
            <Col
              xs={step.photo ? 50 : 100}
              md={step.photo ? 50 : 100}
              sm={step.photo ? 50 : 100}
              lg={step.photo ? 50 : 100}
              alignItems="left"
            >
              <H2 color={Colors.gray}>
                Step {step.index + 1} -
                <H2 color={Colors.black} style={{ marginLeft: 5 }}>
                  {step.title}
                </H2>
              </H2>

              <H3 color={Colors.black}>{step.description}</H3>
            </Col>
            {step.photo && (
              <Col xs={50} md={50} sm={50} lg={50}>
                <img width="250" src={step.photo} />
              </Col>
            )}
          </Row>
          <Line />
        </>
      ))}
    </Modal>
  );

  handleMarkAlertAsViewed = alertId => {
    const { markAlertAsViewed, fetchAlerts, location } = this.props;
    if (alertId) {
      markAlertAsViewed({ alert_id: alertId, location_id: location.id });
      // Refresh alerts after marking as viewed
      fetchAlerts({ location_id: location.id });
    }
  };

  handleMarkAllAlertsAsViewed = () => {
    const { markAlertAsViewed, fetchAlerts, alerts, location } = this.props;
    if (alerts && alerts.length > 0) {
      const alertIds = alerts.map(alert => alert.id).filter(id => id);
      if (alertIds.length > 0) {
        markAlertAsViewed({ alert_ids: alertIds, location_id: location.id });
        // Refresh alerts after marking as viewed
        fetchAlerts({ location_id: location.id });
      }
    }
  };

  renderAlertsModal = () => {
    const { alertsModal } = this.state;
    const {
      fetchAlerts,
      alerts,
      alertsPages,
      alertsLoading,
      alertsCurrentPage,
      location: { id: location_id },
    } = this.props;

    return (
      <LocationAlertsModal
        isVisible={alertsModal}
        alerts={alerts}
        loading={alertsLoading}
        currentPage={alertsCurrentPage}
        totalPages={alertsPages}
        onPageChange={page =>
          fetchAlerts({
            page,
            location_id,
          })
        }
        onClose={() => {
          fetchAlerts({
            page: 1,
            location_id,
          });
          this.switchModal('alertsModal');
        }}
      />
    );
  };

  switchModal = name => {
    const isVisible = this.state[name];

    this.setState({ [name]: !isVisible });
  };

  buildLastHealthCheckTooltipContent = data => {
    if (!data) return '';
    const escapeHtml = text => {
      if (text == null) return '';
      const s = String(text);
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };
    const modeLabel = escapeHtml(
      (data.execution_source && EXECUTION_SOURCE_LABELS[data.execution_source]) ||
        data.execution_source ||
        '—',
    );
    const triggeredBy = escapeHtml(formatTriggeredBy(data.triggered_by));
    const checksList = (data.checks || [])
      .map(c => {
        const label = escapeHtml(CHECK_KEY_LABELS[c.check_key] || c.check_key || '—');
        const isPassed = c.status === 'passed';
        const bgColor = isPassed ? '#09AA36' : '#C80000';
        const checkChar = isPassed ? '✓' : '✗';
        return `
        <div style="align-self: stretch; border-radius: 8px; justify-content: flex-start; align-items: center; gap: 10px; display: inline-flex">
          <div style="width: 24px; height: 24px; background: ${bgColor}; box-shadow: 0px 0px 50px rgba(49, 70, 131, 0.34); border-radius: 9999px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;">
            <span style="color: white; font-size: 12px; line-height: 1; font-weight: bold;">${checkChar}</span>
          </div>
          <div style="flex: 1 1 0; flex-direction: column; justify-content: flex-start; align-items: flex-start; gap: 2px; display: inline-flex">
            <div style="align-self: stretch; color: #1C1C1C; font-size: 16px; font-family: Gotham, sans-serif; font-weight: 325; line-height: 24px; word-wrap: break-word">${label}</div>
          </div>
        </div>`;
      })
      .join('');
    return `
<div style="width: 100%; padding: 16px; background: white; border-radius: 20px; flex-direction: column; justify-content: flex-start; align-items: flex-start; gap: 10px; display: inline-flex; box-sizing: border-box; box-shadow: 0 2px 12px rgba(0,0,0,0.12);">
  <div style="align-self: stretch; justify-content: space-between; align-items: flex-start; display: inline-flex; flex-wrap: wrap; gap: 10px;">
    <div style="height: 24px; padding-top: 4px; padding-bottom: 4px; justify-content: flex-start; align-items: flex-start; gap: 10px; display: flex;">
      <div style="color: #808285; font-size: 14px; font-family: Gotham, sans-serif; font-weight: 325; line-height: 18px; word-wrap: break-word">Mode:</div>
      <div style="color: #1C1C1C; font-size: 14px; font-family: Gotham, sans-serif; font-weight: 400; line-height: 18px; word-wrap: break-word">${modeLabel}</div>
    </div>
    <div style="height: 24px; padding-top: 4px; padding-bottom: 4px; justify-content: flex-start; align-items: center; gap: 10px; display: flex;">
      <div style="color: #808285; font-size: 14px; font-family: Gotham, sans-serif; font-weight: 325; line-height: 18px; word-wrap: break-word">Triggered by:</div>
      <div style="color: #1C1C1C; font-size: 14px; font-family: Gotham, sans-serif; font-weight: 400; line-height: 18px; word-wrap: break-word">${triggeredBy}</div>
    </div>
  </div>
  <div style="width: 335px; max-width: 100%; flex-direction: column; justify-content: flex-start; align-items: flex-start; gap: 10px; display: flex;">
    ${checksList}
  </div>
</div>`.trim();
  };

  locationStatusYesNo = value => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  };

  renderLastHealthCheckPanel = () => {
    const { location, lastHealthCheckForLocation, lastHealthCheckForLocationLoading } = this.props;

    return (
      <BG
        width="100%"
        style={{
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          padding: 24,
          paddingBottom: 48,
          minHeight: 220,
        }}
      >
        <Row style={{ marginBottom: 0 }}>
          <Col lg={50} sm={50} md={50} xs={50} alignItems="flex-start">
            <H3 style={{ fontWeight: 700, fontSize: 20 }}>Last health check</H3>
          </Col>
          <Col lg={50} sm={50} md={50} xs={50} alignItems="flex-end">
            {lastHealthCheckForLocation && lastHealthCheckForLocation.report_id && (
              <H3
                onClick={() =>
                  push(`/health-checks/detail/${lastHealthCheckForLocation.report_id}`)
                }
                weight="bold"
                color={Colors.lightBlue}
                style={{ cursor: 'pointer', fontSize: 16 }}
              >
                View details
              </H3>
            )}
          </Col>
        </Row>
        {lastHealthCheckForLocationLoading ? (
          <Loader size={125} block />
        ) : lastHealthCheckForLocation ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <div style={{ color: Colors.gray, fontSize: 13, marginBottom: 8 }}>
                {moment(
                  lastHealthCheckForLocation.completed_at || lastHealthCheckForLocation.started_at,
                ).format('MM/DD/YYYY hh:mm a')}
              </div>
              <Row
                alignItems="center"
                justifyContent="flex-start"
                style={{ flexWrap: 'wrap', gap: 8 }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 12px',
                    borderRadius: 20,
                    fontSize: 14,
                    fontWeight: 600,
                    background:
                      lastHealthCheckForLocation.overall_status === 'passed'
                        ? '#DFF5E5'
                        : lastHealthCheckForLocation.overall_status === 'failed'
                        ? '#FBC7C7'
                        : '#E8E8E8',
                    color:
                      lastHealthCheckForLocation.overall_status === 'passed'
                        ? '#09AA36'
                        : lastHealthCheckForLocation.overall_status === 'failed'
                        ? '#C80000'
                        : Colors.gray8,
                  }}
                >
                  {lastHealthCheckForLocation.overall_status === 'passed'
                    ? 'Pass'
                    : lastHealthCheckForLocation.overall_status === 'failed'
                    ? 'Failed'
                    : 'Skipped'}
                </span>
                {lastHealthCheckForLocation.checks && lastHealthCheckForLocation.checks.length > 0 && (
                  <>
                    <span
                      data-tooltip-id="last-health-check-tooltip"
                      data-tooltip-html={this.buildLastHealthCheckTooltipContent(
                        lastHealthCheckForLocation,
                      )}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: Colors.lightBlue,
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      i
                    </span>
                    <Tooltip
                      id="last-health-check-tooltip"
                      place="bottom"
                      className="last-health-check-tooltip"
                      noArrow={false}
                      arrowColor="#ffffff"
                    />
                    <LastHealthCheckTooltipStyles />
                  </>
                )}
              </Row>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '32px 0',
              color: Colors.gray3,
              fontSize: 16,
            }}
          >
            No health check data
          </div>
        )}
        {location.location_status_confirmed_at && (
          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8, color: '#222' }}>
              Install — location status
            </div>
            <div style={{ color: Colors.gray, fontSize: 13, marginBottom: 10 }}>
              {moment(location.location_status_confirmed_at).format('MM/DD/YYYY hh:mm a')}
            </div>
            <div style={{ fontSize: 14, color: '#222', lineHeight: 1.75 }}>
              <div>Water: {this.locationStatusYesNo(location.location_status_water)}</div>
              <div>Internet: {this.locationStatusYesNo(location.location_status_internet)}</div>
              <div>Power: {this.locationStatusYesNo(location.location_status_power)}</div>
            </div>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <H3
            onClick={() => push(`/health-checks?location_id=${location.id}`)}
            weight="bold"
            color={Colors.lightBlue}
            style={{ cursor: 'pointer', fontSize: 16 }}
          >
            Show health check history
          </H3>
        </div>
      </BG>
    );
  };

  handleChange = evt => {
    const { name, value } = evt.target;

    this.setState({ [name]: value });
  };

  handleChangeInstallationStatus = evt => {
    const {
      updateInstallationStatus,
      location: { id },
    } = this.props;
    const installed = evt.target.value === 'true';
    updateInstallationStatus(id, { installed });
  };

  getHealthCheckDeviceType = () => {
    const { location } = this.props;

    if (location.ReverseOsmosisFilterHead) {
      return 'reverse-osmosis';
    }
    if (location.WaterFilterHead) {
      return 'water-filter';
    }
    if (location.SoftenerFilterHead) {
      return 'water-softener';
    }

    return null;
  };

  getReusableManualHealthCheckReportId = deviceType => {
    const { lastHealthCheckForLocation } = this.props;
    const reportTime =
      lastHealthCheckForLocation?.completed_at ||
      lastHealthCheckForLocation?.started_at ||
      lastHealthCheckForLocation?.created_at;

    if (
      !lastHealthCheckForLocation?.report_id ||
      lastHealthCheckForLocation.execution_source !== 'manual' ||
      lastHealthCheckForLocation.device_type !== deviceType ||
      (lastHealthCheckForLocation.check_type &&
        lastHealthCheckForLocation.check_type !== 'system') ||
      !reportTime
    ) {
      return null;
    }

    const minutesSinceReport = moment().diff(moment(reportTime), 'minutes');
    if (
      Number.isNaN(minutesSinceReport) ||
      minutesSinceReport < 0 ||
      minutesSinceReport > MANUAL_HEALTH_CHECK_ATTEMPT_WINDOW_MINUTES
    ) {
      return null;
    }

    return lastHealthCheckForLocation.report_id;
  };

  toggleQuickActions = () => {
    this.setState(prevState => ({
      quickActionsOpen: !prevState.quickActionsOpen,
    }));
  };

  handleQuickAction = action => {
    const { location, showAlert, startHealthCheck, startHealthCheckLoading } = this.props;

    switch (action) {
      case 'export-data': {
        this.switchModal('modalDatePickerCsv');
        break;
      }
      case 'run-health-check': {
        if (startHealthCheckLoading) {
          break;
        }

        const deviceType = this.getHealthCheckDeviceType();

        if (!deviceType) {
          if (showAlert) {
            showAlert('No device found for this location', {
              variant: 'warning',
              timeout: 9,
            });
          }
          break;
        }

        startHealthCheck({
          location_id: location.id,
          device_type: deviceType,
          report_id: this.getReusableManualHealthCheckReportId(deviceType),
        });
        break;
      }
      case 'open-aws': {
        let thingName = '';
        if (location.ReverseOsmosisFilterHead) {
          thingName = location.ReverseOsmosisFilterHead.thing_name;
        } else if (location.WaterFilterHead) {
          thingName = location.WaterFilterHead.thing_name;
        } else if (location.SoftenerFilterHead) {
          thingName = location.SoftenerFilterHead.thing_name;
        }

        if (thingName) {
          try {
            // Updated URL format for AWS IoT console
            const awsUrl = `https://us-east-1.console.aws.amazon.com/iot/home?region=us-east-1#/thing/${thingName}`;
            const newWindow = window.open(awsUrl, '_blank');

            // Check if the window was blocked by a popup blocker
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
              const { showAlert } = this.props;
              if (showAlert) {
                showAlert(
                  'The AWS console window was blocked by your browser. Please allow popups for this site and try again.',
                  { variant: 'warning', timeout: 9 },
                );
              }
            } else {
              console.log('Window opened successfully');

              // Try to detect if the window was redirected/failed
              setTimeout(() => {
                try {
                  if (newWindow.closed) {
                    console.log('Window was closed quickly after opening');
                  }
                } catch (e) {
                  console.log('Cannot access window properties due to cross-origin restrictions');
                }
              }, 1000);
            }
          } catch (error) {
            console.error('Error opening AWS console:', error);
          }
        } else {
          console.warn('No thing_name found for this device.');
        }
        break;
      }
      case 'generate-report':
        // Implement soon
        break;
      case 'configure-device':
        // Implement soon
        break;
      default:
        console.log(`Quick action: ${action}`);
    }

    this.setState({ quickActionsOpen: false });
  };

  updateLastUpdated = timestamp => {
    this.setState({
      lastUpdated: timestamp,
      lastUpdatedLoading: false,
    });
  };

  setLastUpdatedLoading = () => {
    this.setState({ lastUpdatedLoading: true });
  };

  updateTdsValue = tdsValue => {
    this.setState({
      tdsValue,
      tdsLoading: false,
    });
  };

  setTdsLoading = () => {
    this.setState({ tdsLoading: true });
  };

  // Add handler for date picker changes
  handleDate = (value, key) => {
    this.setState({ [key]: value });
  };

  // Add handler for CSV type changes
  changeCsvType = (csvType, nro) => {
    const csvTypes = ['raw_data', 'calculated_data', 'all_data'];
    this.setState({ [csvType]: nro, csvTypeString: csvTypes[nro - 1] });
  };

  // Add handler to generate CSV
  generateCsv = email => {
    const {
      location,
      generateSoftenerCsv,
      generateWaterFilterCsv,
      generateReverseOsmosisCsv,
    } = this.props;
    const { csvTypeString, datePickerFromCsv: from, datePickerToCsv: to } = this.state;

    const { id: location_id } = location;
    let deviceId = '';
    let exportFunction = null;

    if (location.SoftenerFilterHead) {
      deviceId = location.SoftenerFilterHead.device_id;
      exportFunction = generateSoftenerCsv;
    } else if (location.WaterFilterHead) {
      deviceId = location.WaterFilterHead.device_id;
      exportFunction = generateWaterFilterCsv;
    } else if (location.ReverseOsmosisFilterHead) {
      deviceId = location.ReverseOsmosisFilterHead.device_id;
      exportFunction = generateReverseOsmosisCsv;
    }

    if (exportFunction && deviceId) {
      exportFunction({
        device_id: deviceId,
        location_id,
        from,
        to,
        csvContains: csvTypeString,
        email,
      });
    }

    this.switchModal('modalDatePickerCsv');
  };

  handleLocationChange = e => {
    const locationId = e.target.value;
    if (locationId) {
      window.location.href = `/locations/${locationId}`;
    }
  };

  handleBrandClick = () => {
    const { location } = this.props;
    console.log('Brand click - location object:', location);
    console.log('Brand ID:', location?.brand_id);

    if (location && location.brand_id) {
      // Navigate to locations list with brand filter applied
      const url = `/locations?brand_id=${location.brand_id}`;
      console.log('Navigating to:', url);
      push(url);
    } else {
      // Fallback to locations page if no brand_id available
      console.log('No brand_id found, navigating to locations page');
      push('/locations');
    }
  };

  handleAccountClick = () => {
    const { location } = this.props;
    console.log('Account click - location object:', location);
    console.log('Account ID:', location?.account_id);

    if (location && location.account_id) {
      // Navigate to locations list with account filter applied
      const url = `/locations?account_id=${location.account_id}`;
      console.log('Navigating to:', url);
      push(url);
    } else {
      // Fallback to locations page if no account_id available
      console.log('No account_id found, navigating to locations page');
      push('/locations');
    }
  };

  setDeviceDisconnected = isDisconnected => {
    this.setState({ isDisconnected });
  };

  render() {
    const {
      filterHeadSelected,
      quickActionsOpen,
      lastUpdated,
      lastUpdatedLoading,
      tdsValue,
      tdsLoading,
      isDisconnected,
    } = this.state;
    const {
      location,
      alerts,
      alertsLoading,
      role_id,
      role_code,
      relatedLocations,
      startHealthCheckLoading,
    } = this.props;
    const isAdmin = role_code === 'admin';
    const {
      identificator,
      brand_name,
      account_name,
      full_address,
      WaterFilterHead,
      ReverseOsmosisFilterHead,
      SoftenerFilterHead,
    } = location;

    const filterHeads = [];

    if (WaterFilterHead) {
      filterHeads.push({
        label: `${WaterFilterHead.WaterFilterConfiguration.name} (${WaterFilterHead.chlorine_or_chloramine})`,
        value: 'WaterFilterHead',
      });
    }
    if (ReverseOsmosisFilterHead) {
      filterHeads.push({
        label: `${ReverseOsmosisFilterHead.ReverseOsmosisFilterConfiguration.name}`,
        value: 'ReverseOsmosisFilterHead',
      });
    }
    if (SoftenerFilterHead) {
      filterHeads.push({
        label: `${SoftenerFilterHead.SoftenerConfiguration.name} (${SoftenerFilterHead.chlorine_or_chloramine})`,
        value: 'SoftenerFilterHead',
      });
    }

    const selected = filterHeadSelected || (filterHeads.length ? filterHeads[0].value : '');
    return (
      <Container style={{ paddingLeft: 15, paddingRight: 15, paddingBottom: 24 }}>
        {/* Breadcrumbs Navigation */}
        <Row
          justifyContent="space-between"
          alignItems="center"
          style={{
            padding: '5px 0',
            marginBottom: '0px',
            display: 'flex',
            flexWrap: 'nowrap',
          }}
        >
          <Row
            justifyContent="flex-start"
            alignItems="center"
            style={{ flexWrap: 'nowrap', flex: '1 1 auto', overflow: 'hidden' }}
          >
            <Icon
              customStyle={{ marginRight: '8px', cursor: 'pointer' }}
              onClick={() => push('/locations')}
              name="chevron-left"
              color={Colors.blue}
              fontSize={16}
            />

            {/* Vertical divider */}
            <div
              style={{
                width: '1px',
                height: '20px',
                backgroundColor: '#DDDDDD',
                margin: '0 12px',
                display: 'inline-block',
              }}
            />

            <H5
              color={Colors.gray}
              style={{ margin: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={this.handleBrandClick}
            >
              {brand_name}
            </H5>
            <Icon
              name="chevron-right"
              color={Colors.gray}
              fontSize={12}
              customStyle={{ margin: '0 8px' }}
            />
            <H5
              color={Colors.gray}
              style={{ margin: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={this.handleAccountClick}
            >
              {account_name}
            </H5>
            <Icon
              name="chevron-right"
              color={Colors.gray}
              fontSize={12}
              customStyle={{ margin: '0 8px' }}
            />
            {relatedLocations && relatedLocations.length > 0 ? (
              <BreadcrumbWrapper>
                <span style={{ marginRight: '8px', fontFamily: 'Inter, sans-serif' }}>
                  {identificator}
                </span>
                <Icon name="sort" color={Colors.gray} fontSize={14} />
                <BreadcrumbSelect value={location.id} onChange={this.handleLocationChange}>
                  <option key={location.id} value={location.id}>
                    {location.WaterFilterHead
                      ? 'Water Filter: '
                      : location.ReverseOsmosisFilterHead
                      ? 'RO: '
                      : location.SoftenerFilterHead
                      ? 'Softener: '
                      : ''}
                    {identificator}
                  </option>
                  {relatedLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.WaterFilterHead
                        ? 'Water Filter: '
                        : loc.ReverseOsmosisFilterHead
                        ? 'RO: '
                        : loc.SoftenerFilterHead
                        ? 'Softener: '
                        : ''}
                      {loc.identificator}
                    </option>
                  ))}
                </BreadcrumbSelect>
              </BreadcrumbWrapper>
            ) : (
              <H5
                color={Colors.black}
                weight={600}
                style={{ margin: 0, whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif' }}
              >
                {identificator}
              </H5>
            )}
          </Row>

          {/* Actions Section */}
          <div
            style={{
              display: 'flex',
              marginLeft: 'auto',
              gap: 0,
            }}
          >
            <div style={{ marginRight: '6px' }}>
              <IconButton
                name="info"
                size={33}
                fontSize={15}
                onClick={() => this.switchModal('showContactInfo')}
              />
            </div>
            <QuickActionsDropdown>
              <IconButton
                name="ellipsis-v"
                size={33}
                fontSize={15}
                onClick={this.toggleQuickActions}
              />
              <DropdownContent isOpen={quickActionsOpen}>
                <DisabledDropdownItem>
                  <Icon
                    name="chart-bar"
                    color={Colors.gray3}
                    fontSize={14}
                    customStyle={{ marginRight: '8px' }}
                  />
                  Generate Report
                </DisabledDropdownItem>
                {isAdmin ? (
                  <DropdownItem onClick={() => this.handleQuickAction('export-data')}>
                    <Icon
                      name="file-export"
                      color={Colors.gray}
                      fontSize={14}
                      customStyle={{ marginRight: '8px' }}
                    />
                    Export Data
                  </DropdownItem>
                ) : (
                  <DisabledDropdownItem>
                    <Icon
                      name="file-export"
                      color={Colors.gray3}
                      fontSize={14}
                      customStyle={{ marginRight: '8px' }}
                    />
                    Export Data
                  </DisabledDropdownItem>
                )}

                {KineticoRolesCode.includes(role_code) &&
                  (startHealthCheckLoading ? (
                    <DisabledDropdownItem>
                      <Icon
                        name="heartbeat"
                        color={Colors.gray3}
                        fontSize={14}
                        customStyle={{ marginRight: '8px' }}
                      />
                      Running Health Check
                    </DisabledDropdownItem>
                  ) : (
                    <DropdownItem onClick={() => this.handleQuickAction('run-health-check')}>
                      <Icon
                        name="heartbeat"
                        color={Colors.gray}
                        fontSize={14}
                        customStyle={{ marginRight: '8px' }}
                      />
                      Run Health Check
                    </DropdownItem>
                  ))}

                {role_id === 1 && (
                  <DropdownItem onClick={() => this.handleQuickAction('open-aws')}>
                    <Icon
                      name="cloud"
                      color={Colors.gray}
                      fontSize={14}
                      customStyle={{ marginRight: '8px' }}
                    />
                    Open in AWS
                  </DropdownItem>
                )}
              </DropdownContent>
            </QuickActionsDropdown>
          </div>
        </Row>
        <Line style={{ marginBottom: '10px', marginTop: '5px' }} />
        <Row justifyContent="flex-start" alignItems="center" style={{ marginBottom: '20px' }}>
          <H2 color={Colors.black} weight={600} style={{ fontFamily: 'Inter, sans-serif' }}>
            {identificator}
          </H2>
        </Row>
        <Row justifyContent="flex-start" alignItems="center" style={{ marginBottom: '20px' }}>
          <Icon
            name="map-marker-alt"
            color={Colors.gray8}
            fontSize={16}
            customStyle={{ marginRight: '8px' }}
          />
          <H4 color={Colors.gray8}>{full_address}</H4>

          {ReverseOsmosisFilterHead && (
            <div
              style={{
                marginLeft: '15px',
                background: Colors.gray2,
                padding: '4px 10px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                width: tdsValue ? 'auto' : '100px',
                maxWidth: tdsValue ? '200px' : '100px',
                height: '28px',
                minWidth: '100px',
              }}
            >
              {tdsLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    width: '100%',
                    height: '100%',
                    background:
                      'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.5), rgba(255,255,255,0))',
                    animation: 'shimmer 1.5s infinite',
                    zIndex: 1,
                    transform: 'translateX(-100%)',
                  }}
                />
              )}
              <Icon
                name="tint"
                color={Colors.gray8}
                fontSize={12}
                customStyle={{
                  marginRight: '5px',
                  position: 'relative',
                  zIndex: 2,
                }}
              />
              <H5
                color={Colors.gray8}
                style={{
                  margin: 0,
                  position: 'relative',
                  zIndex: 2,
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                TDS:
                {isDisconnected ? (
                  <span
                    style={{
                      marginLeft: '4px',
                      display: 'inline-block',
                      color: Colors.gray8,
                    }}
                  >
                    N/A
                  </span>
                ) : tdsValue !== null ? (
                  <span
                    style={{
                      opacity: tdsValue !== null ? 1 : 0,
                      transition: 'opacity 0.5s ease-in',
                      marginLeft: '4px',
                      display: 'inline-block',
                      color: Colors.green,
                    }}
                  >
                    {tdsValue || 'N/A'}
                  </span>
                ) : (
                  <span
                    style={{
                      opacity: tdsLoading ? 1 : 0,
                      transition: 'opacity 0.5s ease-in',
                      marginLeft: '4px',
                      display: 'inline-block',
                    }}
                  >
                    ...
                  </span>
                )}
              </H5>
            </div>
          )}

          <div
            style={{
              marginLeft: '15px',
              background: isDisconnected ? '#FFEBEE' : Colors.gray2,
              padding: '4px 10px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              width: lastUpdated || isDisconnected ? 'auto' : '120px',
              maxWidth: lastUpdated || isDisconnected ? '320px' : '120px',
              height: '28px',
              minWidth: '120px',
            }}
            data-tooltip-id={isDisconnected ? 'disconnected-tooltip' : ''}
          >
            {lastUpdatedLoading && !isDisconnected && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.5), rgba(255,255,255,0))',
                  animation: 'shimmer 1.5s infinite',
                  zIndex: 1,
                  transform: 'translateX(-100%)',
                }}
              />
            )}
            <Icon
              name={isDisconnected ? 'exclamation-circle' : 'sync'}
              color={isDisconnected ? Colors.red : Colors.gray8}
              fontSize={12}
              customStyle={{
                marginRight: '5px',
                animation:
                  lastUpdatedLoading && !isDisconnected ? 'spin 1.5s linear infinite' : 'none',
                position: 'relative',
                zIndex: 2,
              }}
            />
            <H5
              color={isDisconnected ? Colors.red : Colors.gray8}
              style={{
                margin: 0,
                position: 'relative',
                zIndex: 2,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isDisconnected ? (
                'Disconnected'
              ) : (
                <>
                  Last update:
                  {lastUpdated && (
                    <span
                      style={{
                        opacity: lastUpdated ? 1 : 0,
                        transition: 'opacity 0.5s ease-in',
                        marginLeft: '4px',
                        display: 'inline-block',
                      }}
                    >
                      {moment.unix(lastUpdated).format('MMM DD, YYYY h:mm a')}
                    </span>
                  )}
                </>
              )}
            </H5>
          </div>

          {isDisconnected && (
            <Tooltip
              id="disconnected-tooltip"
              content="This location is currently disconnected. Some data may be outdated."
              place="bottom"
            />
          )}

          <style jsx="true">{`
            @keyframes shimmer {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(100%);
              }
            }
            @keyframes spin {
              0% {
                transform: rotate(0deg);
              }
              100% {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </Row>

        {location.installed ? (
          this.switchByFilterHead(selected)
        ) : (
          <InstallationNotCompleteContainer>
            <ListEmpty message="Installation Not Complete" />
          </InstallationNotCompleteContainer>
        )}
        {location.installed && (
          <Row style={{ marginTop: 24, marginBottom: 32 }} alignItems="stretch">
            <Col lg={50} sm={100} md={50} xs={100} style={{ paddingRight: 12 }}>
              <BG
                width="100%"
                style={{
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                  padding: 24,
                  paddingBottom: 48,
                  minHeight: 220,
                }}
              >
                <Row style={{ marginBottom: 0 }}>
                  <Col lg={50} sm={50} md={50} xs={50} alignItems="flex-start">
                    <H3 style={{ fontWeight: 700, fontSize: 20 }}>Alerts</H3>
                  </Col>
                  <Col lg={50} sm={50} md={50} xs={50} alignItems="flex-end">
                    <H3
                      onClick={() => {
                        const { fetchAlerts, location } = this.props;
                        fetchAlerts({ location_id: location.id, page: 1 });
                        this.switchModal('alertsModal');
                      }}
                      weight="bold"
                      color={Colors.lightBlue}
                      style={{ cursor: 'pointer', fontSize: 16 }}
                    >
                      Open More
                    </H3>
                  </Col>
                </Row>
                {alertsLoading ? (
                  <Loader size={125} block />
                ) : alerts && alerts.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {alerts.slice(0, 2).map((alert, idx) => (
                      <LocationAlertCard
                        key={alert.id || idx}
                        alert={alert}
                        compact
                        showBadge={false}
                      />
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '32px 0',
                      color: Colors.gray3,
                      fontSize: 16,
                    }}
                  >
                    There are currently no alerts
                  </div>
                )}
              </BG>
            </Col>
            <Col lg={50} sm={100} md={50} xs={100} style={{ paddingLeft: 12 }}>
              {this.renderLastHealthCheckPanel()}
            </Col>
          </Row>
        )}
        {this.renderContactInfoDrawer()}
        {this.renderAlertsModal()}

        {/* Add Modal for Export Data */}
        <ModalCsvFiles
          isVisible={this.state.modalDatePickerCsv}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromCsv')}
          handleDateTo={value => this.handleDate(value, 'datePickerToCsv')}
          switchModal={() => this.switchModal('modalDatePickerCsv')}
          dateFrom={this.state.datePickerFromCsv}
          dateTo={this.state.datePickerToCsv}
          changeCsvType={this.changeCsvType}
          csvType={this.state.csvType}
          userEmail={this.props.userEmail}
          saveChanges={this.generateCsv}
          minDate={location.installed_date && moment(location.installed_date).toDate()}
        />
      </Container>
    );
  }
}

WaterFilterForm.propTypes = {
  alerts: PropTypes.array,
  alertsCurrentPage: PropTypes.number,
  alertsLoading: PropTypes.bool,
  alertsPages: PropTypes.number,
  allAlerts: PropTypes.array,
  fetchAlerts: PropTypes.func,
  fetchAllAlerts: PropTypes.func,
  generateReverseOsmosisCsv: PropTypes.func,
  generateSoftenerCsv: PropTypes.func,
  generateWaterFilterCsv: PropTypes.func,
  lastHealthCheckForLocation: PropTypes.object,
  lastHealthCheckForLocationLoading: PropTypes.bool,
  location: PropTypes.object,
  markAlertAsViewed: PropTypes.func,
  relatedLocations: PropTypes.array,
  role_code: PropTypes.string,
  role_id: PropTypes.number,
  setDeviceId: PropTypes.func,
  showAlert: PropTypes.func,
  startHealthCheck: PropTypes.func,
  startHealthCheckLoading: PropTypes.bool,
  updateInstallationStatus: PropTypes.func,
  userEmail: PropTypes.string,
};

WaterFilterForm.defaultProps = {
  relatedLocations: [],
};
